import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  Chip,
  Typography,
} from '@mui/joy';
import AccordionSummary, {
  accordionSummaryClasses,
} from '@mui/joy/AccordionSummary';
import { CalendarPlus, Info, Phone, VideoCamera } from '@phosphor-icons/react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { Alert, AlertTitle } from '~/components/ui/alert';
import { Card } from '~/components/ui/card';
import { TextLink } from '~/components/ui/link';
import { ROLE_VALUES } from '~/constants/customerConstant';
import { CALL_VALUES, MEETING_RESPONSE } from '~/constants/meetings';
import { useTenantSlug } from '~/features/tenant/helpers';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import QueryResolver from '~/services/api/query-resolver';
import { ReportService } from '~/services/api/reports/endpoints';
import { ScheduleService } from '~/services/api/schedules/endpoints';
import { ScheduledMeeting } from '~/services/api/schedules/types';
import { useAuthSlice } from '~/slices/auth';
import { formatDate, formatDateTime, formatTime } from '~/utils/helpers';

function MeetingOrganizer({ meeting }: { meeting: ScheduledMeeting }) {
  const userType = useAuthSlice().auth.user.type;
  const isWhistleBlower = ROLE_VALUES.WHISTLE_BLOWER === userType;
  const isAdmin =
    ROLE_VALUES.PARTNER_ADMIN === userType ||
    ROLE_VALUES.TENANT_ADMIN === userType;

  if (
    meeting.created_by?.user_type === ROLE_VALUES.WHISTLE_BLOWER &&
    isWhistleBlower
  ) {
    return 'You';
  } else if (meeting.created_by?.user_type === ROLE_VALUES.WHISTLE_BLOWER) {
    return `${meeting.created_by?.full_name ?? 'Anonymous'} Whistleblower`;
  } else if (
    meeting.created_by?.user_type !== ROLE_VALUES.WHISTLE_BLOWER &&
    !isAdmin
  ) {
    return meeting.created_by?.full_name ?? 'You';
  } else {
    return meeting.created_by?.full_name ?? 'No Name';
  }
}

export function ScheduledReportMeetings() {
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();
  const referenceId = useParams().reportId ?? '';
  const slug = useTenantSlug();
  const reportId = ReportService.endpoints.report.useQuery({
    tenantSlug: slug,
    referenceId,
  }).data?.id;

  const query = ScheduleService.endpoints.reportSchedules.useQuery(
    {
      reportId: reportId ?? '',
    },
    { skip: !reportId },
  );

  const getMeetingLink = (meetingLink?: string | null): string => {
    if (meetingLink) {
      const urlArray = meetingLink.split('/');
      const meetingCode = urlArray[urlArray.length - 1];
      return `/meetings/${meetingCode}`;
    } else {
      createToastNotification(
        intl.formatMessage({
          defaultMessage:
            'A meeting link will be created once all attendees have accepted the invite.',
        }),
        'error',
      );
      return '';
    }
  };

  return (
    <QueryResolver query={query}>
      {(meetings) =>
        meetings.count === 0 ? (
          <Alert variant='plain' startDecorator={<Info />}>
            <AlertTitle>
              {intl.formatMessage({
                defaultMessage: 'No Scheduled In-Person Meetings Found',
              })}
            </AlertTitle>
          </Alert>
        ) : (
          <AccordionGroup>
            {meetings.results
              .filter((meeting) => {
                return meeting.attendees.some(({ attendee, confirm }) => {
                  return (
                    confirm !== MEETING_RESPONSE.DECLINE &&
                    attendee?.user_type === ROLE_VALUES.WHISTLE_BLOWER
                  );
                });
              })
              .map((meeting) => {
                const isInPersion = meeting.type === CALL_VALUES.IN_PERSON;
                const isVoice = meeting.type === CALL_VALUES.VOICE;
                const isVideo = meeting.type === CALL_VALUES.VIDEO;
                const isAccepted = meeting.attendees.some(
                  ({ attendee, confirm }) => {
                    return (
                      confirm === MEETING_RESPONSE.CONFIRM &&
                      attendee?.user_type === ROLE_VALUES.WHISTLE_BLOWER
                    );
                  },
                );

                return (
                  <Accordion
                    key={meeting.id}
                    slotProps={{
                      root: {
                        onTransitionEnd: () => {
                          const summaryNode = document.getElementById(
                            meeting.id.toString(),
                          );
                          if (summaryNode) {
                            summaryNode.scrollIntoView({
                              behavior: 'smooth',
                              block: 'start',
                            });
                          }
                        },
                      },
                    }}
                  >
                    <AccordionSummary
                      slotProps={{
                        button: { className: 'group' },
                      }}
                      indicator={
                        <div className='text-sm font-semibold hover:underline'>
                          <span className='group-aria-expanded:hidden'>
                            {intl.formatMessage({ defaultMessage: 'Show' })}
                          </span>
                          <span className='hidden group-aria-expanded:block'>
                            {intl.formatMessage({ defaultMessage: 'Hide' })}
                          </span>
                        </div>
                      }
                      sx={{
                        [`& [aria-expanded="true"] .${accordionSummaryClasses.indicator}`]:
                          {
                            transform: 'none',
                          },
                      }}
                    >
                      <span className='flex gap-1 items-center'>
                        <Typography level='title-sm'>
                          <MeetingOrganizer meeting={meeting} />
                        </Typography>
                        <span>&middot;</span>
                        <Typography level='body-sm'>
                          {formatDateTime(new Date(meeting.created_at))}
                        </Typography>
                      </span>
                    </AccordionSummary>
                    <AccordionDetails id={meeting.id.toString()}>
                      <Card size='md' className='w-96'>
                        <div className='flex flex-row justify-between items-center'>
                          <Typography
                            level='title-sm'
                            startDecorator={<CalendarPlus />}
                          >
                            {isInPersion
                              ? intl.formatMessage({
                                  defaultMessage:
                                    'Scheduled in-person meet invitation:',
                                })
                              : intl.formatMessage({
                                  defaultMessage: 'Scheduled meet invitation:',
                                })}
                          </Typography>
                          <div>
                            {isVideo && (
                              <TextLink
                                level='title-sm'
                                href={getMeetingLink(meeting.link)}
                                startDecorator={<VideoCamera color='black' />}
                              >
                                {intl.formatMessage({
                                  defaultMessage: 'Video call',
                                })}
                              </TextLink>
                            )}
                            {isVoice && (
                              <TextLink
                                level='title-sm'
                                href={getMeetingLink(meeting.link)}
                                startDecorator={<Phone color='black' />}
                              >
                                {intl.formatMessage({
                                  defaultMessage: 'Voice call',
                                })}
                              </TextLink>
                            )}
                          </div>
                        </div>
                        <div className='flex'>
                          <dl className='flex-1'>
                            <div className='flex gap-4'>
                              <dt className='w-16 shrink-0'>
                                <Typography level='body-sm'>
                                  {intl.formatMessage({
                                    defaultMessage: 'Date',
                                  })}
                                </Typography>
                              </dt>
                              <dd>
                                <Typography level='title-sm'>
                                  {formatDate(new Date(meeting.date_and_time))}
                                </Typography>
                              </dd>
                            </div>
                            <div className='flex gap-4'>
                              <dt className='w-16 shrink-0'>
                                <Typography level='body-sm'>
                                  {intl.formatMessage({
                                    defaultMessage: 'Time',
                                  })}
                                </Typography>
                              </dt>
                              <dd>
                                <Typography level='title-sm'>
                                  {formatTime(new Date(meeting.date_and_time))}
                                </Typography>
                              </dd>
                            </div>
                            {isInPersion && (
                              <div className='flex gap-4'>
                                <dt className='w-16 shrink-0'>
                                  <Typography level='body-sm'>
                                    {intl.formatMessage({
                                      defaultMessage: 'Location',
                                    })}
                                  </Typography>
                                </dt>
                                <dd>
                                  <Typography level='title-sm'>
                                    {meeting.address}
                                  </Typography>
                                </dd>
                              </div>
                            )}
                            {!isInPersion && (
                              <div className='flex gap-4'>
                                <dt className='w-16 shrink-0'>
                                  <Typography level='body-sm'>
                                    {intl.formatMessage({
                                      defaultMessage: 'Guest',
                                    })}
                                  </Typography>
                                </dt>
                                <dd>
                                  <Typography level='title-sm'>
                                    {meeting.guest_attendees_count}
                                    {' '}
                                    {intl.formatMessage({
                                      defaultMessage: 'guest/s invite',
                                    })}
                                  </Typography>
                                </dd>
                              </div>
                            )}
                          </dl>
                          <div className='flex items-end'>
                            {isAccepted ? (
                              <Chip color='success'>
                                {intl.formatMessage({
                                  defaultMessage: 'Accepted',
                                })}
                              </Chip>
                            ) : (
                              <Chip color='warning'>
                                {intl.formatMessage({
                                  defaultMessage: 'Pending',
                                })}
                              </Chip>
                            )}
                          </div>
                        </div>
                      </Card>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
          </AccordionGroup>
        )
      }
    </QueryResolver>
  );
}
